// import instance from "./axiosInstance";
import axios from "axios";
import { api } from "./api_route";

/**
 * RESPONDENT DETAILS API
 * Handles all respondent-related API operations
 */

// ============================================================================
// RESPONDENT DETAILS OPERATIONS
// ============================================================================

/**
 * Create a new respondent
 * @param {Object} respondentData - Respondent details object
 * @returns {Promise}
 */
export const createRespondentDetailsAPI = async (respondentData) => {
  try {
    const config = {};
    if (typeof FormData !== "undefined" && respondentData instanceof FormData) {
      config.headers = { "Content-Type": "multipart/form-data" };
    }

    const response = await axios.post(
      `${api}/respondentDetails/create`,
      respondentData,
      config,
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

/**
 * Update respondent details
 * @param {number} respondentDetailsID - Respondent ID
 * @param {Object} respondentData - Updated respondent details
 * @returns {Promise}
 */
export const updateRespondentDetailsAPI = async (
  respondentDetailsID,
  respondentData,
) => {
  try {
    const config = {};
    if (typeof FormData !== "undefined" && respondentData instanceof FormData) {
      config.headers = { "Content-Type": "multipart/form-data" };
    }

    const response = await axios.put(
      `${api}/respondentDetails/update/${respondentDetailsID}`,
      respondentData,
      config,
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

/**
 * Delete respondent details (soft delete)
 * @param {number} respondentDetailsID - Respondent ID
 * @returns {Promise}
 */
export const deleteRespondentDetailsAPI = async (respondentDetailsID) => {
  try {
    const response = await axios.delete(
      `${api}/respondentDetails/delete/${respondentDetailsID}`,
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

/**
 * Get respondent details by ID
 * @param {number} respondentDetailsID - Respondent ID
 * @returns {Promise}
 */
export const getRespondentDetailsAPI = async (respondentDetailsID) => {
  try {
    const response = await axios.get(
      `${api}/respondentDetails/get/${respondentDetailsID}`,
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

/**
 * Get all respondents for a specific case
 * @param {number} caseID - Case ID
 * @returns {Promise}
 */
export const getRespondentsByCaseIDAPI = async (caseID) => {
  try {
    const response = await axios.get(`${api}/respondentDetails/case/${caseID}`);
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

// ============================================================================
// RESPONDENT DOCUMENTS OPERATIONS
// ============================================================================

/**
 * Upload respondent documents
 * @param {FormData} formData - FormData with file and metadata
 * @returns {Promise}
 */
export const uploadRespondentDocumentAPI = async (formData) => {
  try {
    const response = await axios.post(
      `${api}/respondentDetails/document/create`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

/**
 * Update respondent document
 * @param {number} documentID - Document ID
 * @param {Object} documentData - Document metadata to update
 * @returns {Promise}
 */
export const updateRespondentDocumentAPI = async (documentID, documentData) => {
  try {
    const response = await axios.put(
      `${api}/respondentDetails/document/update/${documentID}`,
      documentData,
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

/**
 * Delete respondent document (soft delete)
 * @param {number} documentID - Document ID
 * @returns {Promise}
 */
export const deleteRespondentDocumentAPI = async (documentID) => {
  try {
    const response = await axios.delete(
      `${api}/respondentDetails/document/delete/${documentID}`,
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

/**
 * Get respondent documents
 * @param {number} respondentDetailsID - Respondent ID
 * @returns {Promise}
 */
export const getRespondentDocumentsAPI = async (respondentDetailsID) => {
  try {
    const response = await axios.get(
      `${api}/respondentDetails/documents/${respondentDetailsID}`,
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

export const respondentDocumentDownload = async (values) => {
  try {
    const response = await axios.get(
      `${api}/documents/respondentDocuments/${values.original.documentNewName}`,
      {
        responseType: "blob",
      },
    );

    return response.data;
  } catch (error) {
    return [];
  }
};

// Document Categories Constants
export const RESPONDENT_DOCUMENT_CATEGORIES = {
  AADHAAR_ATTACHMENT: 1237,
  PAN_ATTACHMENT: 1238,
  CIN_GSTIN: 1239,
  INCORPORATE_CERTIFICATE: 1240,
  OTHERS: 1241,
};

export const DOCUMENT_CATEGORY_LABELS = {
  1237: "Aadhaar Attachment",
  1238: "PAN Attachment",
  1239: "CIN/GSTIN",
  1240: "Incorporate Certificate",
  1241: "Others",
};
